import { ArrowLeftIcon, InfoIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useConfig } from '@entities/config/api';
import { acceptanceBlocked, useAcknowledgeDiscrepancy, useConclusion } from '@entities/conclusion';
import {
  awaitingActionKey,
  awaitingReasonKey,
  checkFindings,
  closeReasonKey,
  currentMandate,
  decidableOffer,
  nextActorKey,
  phaseKey,
  useCreateMandate,
  useDeclineRequest,
  useMessageAction,
  useNegotiation,
  useNegotiationMetrics,
  useNegotiationPreview,
  usePatchNegotiation,
  useRetryDraft,
  useUserReply,
  type NegotiationDetail,
  type NegotiationPatchAction,
} from '@entities/negotiation';
import { useSearchSession } from '@entities/search-session';
import { useSelection, useSelectVehicle } from '@entities/selection';
import { KarlDialogue } from '@features/chat';
import { FinalCheckBlock } from '@features/conclusion';
import {
  BargainingFeed,
  BargainingSetup,
  CheckAnswers,
  NegotiationStatusBadge,
  OfferDecision,
  SellerMetrics,
  type FeeTerms,
} from '@features/negotiation';
import { queryKeys } from '@shared/api/queryKeys';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Badge, Button, ButtonLink, Skeleton, StateBlock } from '@shared/ui';

import styles from './NegotiationPage.module.css';

/**
 * Таймер окна отмены (FE-180, §23.7).
 *
 * Дедлайн — серверный `scheduled_send_at`, а не локальный отсчёт: локальные
 * часы лишь двигают стрелку между перерисовками. Пока письмо `scheduled`,
 * карточка опрашивается каждые 2 секунды (см. `useNegotiation`), поэтому
 * отправка и отмена видны без перезагрузки. Письма торга уходят без окна
 * (12.09.2026), и блок появляется только в режиме подтверждения.
 */
function ScheduledCountdown({ scheduledAt }: { scheduledAt: string | null }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  if (scheduledAt === null) return <>{t('negotiation.detail.scheduled')}</>;
  const remainingMs = new Date(scheduledAt).getTime() - now;
  if (remainingMs <= 0) return <>{t('negotiation.detail.sendingSoon')}</>;
  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);
  return <>{t('negotiation.detail.scheduledIn', { minutes, seconds })}</>;
}

/**
 * Экран `awaiting_user` для причин **не про цену** (FE-180, MVP_PRD §22.5):
 * просьба контакта, тема вне списка, подозрительное письмо, отказ guardrails.
 * Решение по предложению живёт в ленте торга — здесь оно не дублируется.
 *
 * Не более трёх вариантов действия, и **у каждого кода есть действие**:
 * кнопка, которая ничего не делает, — обещание отсутствующего функционала
 * (CLAUDE.md §2). `DECLINE_REQUEST` уходит письмом и возвращает переписку в
 * работу; `REPLY_MANUALLY` раскрывает поле для собственного текста — оно
 * проходит те же guardrails, что и письмо агента; `CONTINUE` — возобновить
 * без письма; `UPDATE_MANDATE` открывает опросник встречного предложения;
 * `RETRY_DRAFT` — Karl составляет письмо заново с теми же проверками (BE-602):
 * сервер предлагает его, только пока не исчерпан лимит автономных ответов.
 */
function AwaitingUserPanel({
  detail,
  onStop,
  onResume,
  onUpdateMandate,
  busy,
}: {
  detail: NegotiationDetail;
  onStop: () => void;
  onResume: () => void;
  onUpdateMandate: (() => void) | null;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const declineRequest = useDeclineRequest(detail.negotiation_id);
  const userReply = useUserReply(detail.negotiation_id);
  const retryDraft = useRetryDraft(detail.negotiation_id);
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState('');
  const top4Route =
    detail.search_session_id === null ? null : ROUTES.searchTop4(detail.search_session_id);
  const pending = busy || declineRequest.isPending || userReply.isPending || retryDraft.isPending;
  const failure = actionError(declineRequest.error ?? userReply.error ?? retryDraft.error, t);

  const renderAction = (code: string) => {
    const label = t(awaitingActionKey(code));
    switch (code) {
      case 'STOP_NEGOTIATION':
        return (
          <Button key={code} variant="danger" disabled={pending} onClick={onStop}>
            {label}
          </Button>
        );
      case 'CONTINUE':
      case 'CONTINUE_BARGAINING':
        return (
          <Button key={code} variant="secondary" disabled={pending} onClick={onResume}>
            {label}
          </Button>
        );
      case 'DECLINE_REQUEST':
        return (
          <Button
            key={code}
            variant="secondary"
            disabled={pending}
            onClick={() => {
              declineRequest.mutate();
            }}
          >
            {declineRequest.isPending ? t('negotiation.detail.pending') : label}
          </Button>
        );
      case 'RETRY_DRAFT':
        return (
          <Button
            key={code}
            variant="secondary"
            disabled={pending}
            onClick={() => {
              retryDraft.mutate();
            }}
          >
            {retryDraft.isPending ? t('negotiation.detail.pending') : label}
          </Button>
        );
      case 'REPLY_MANUALLY':
        return (
          <Button
            key={code}
            variant="secondary"
            disabled={pending}
            aria-expanded={replying}
            onClick={() => {
              setReplying((value) => !value);
            }}
          >
            {label}
          </Button>
        );
      case 'UPDATE_MANDATE':
      case 'SWITCH_SUPERVISED':
        return onUpdateMandate === null ? null : (
          <Button key={code} variant="secondary" disabled={pending} onClick={onUpdateMandate}>
            {label}
          </Button>
        );
      case 'SELECT_VEHICLE':
      case 'REPLACE_VEHICLE':
        return top4Route === null ? null : (
          <ButtonLink key={code} variant="secondary" to={top4Route}>
            {label}
          </ButtonLink>
        );
      default:
        // `REVIEW_OFFER` и `DECLINE_OFFER` живут в ленте торга.
        return null;
    }
  };

  return (
    <section className={styles.pending} aria-label={t('negotiation.detail.awaitingTitle')}>
      <p className={styles.pendingText}>{t('negotiation.detail.awaitingHint')}</p>
      <div className={styles.actions}>{detail.awaiting_actions.map(renderAction)}</div>

      {replying ? (
        <form
          className={styles.replyForm}
          onSubmit={(event) => {
            event.preventDefault();
            userReply.mutate(text, {
              onSuccess: () => {
                setReplying(false);
                setText('');
              },
            });
          }}
        >
          <label className={styles.replyLabel} htmlFor="negotiation-user-reply">
            {t('negotiation.reply.label')}
          </label>
          <textarea
            id="negotiation-user-reply"
            className={styles.replyField}
            value={text}
            rows={6}
            maxLength={1100}
            required
            minLength={20}
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          <p className={styles.pendingText}>{t('negotiation.reply.hint')}</p>
          <div className={styles.actions}>
            <Button type="submit" disabled={pending || text.trim().length < 20}>
              {userReply.isPending ? t('negotiation.reply.pending') : t('negotiation.reply.send')}
            </Button>
          </div>
        </form>
      ) : null}

      {failure === null ? null : (
        <p className={styles.error} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          <span>{failure}</span>
        </p>
      )}
    </section>
  );
}

/**
 * Текст отказа по коду ответа. Код без перевода не показывается — есть общий
 * запасной текст; провал guardrails перечисляет нарушенные правила.
 */
function actionError(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `negotiation.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('negotiation.error.text') : translated;
}

/**
 * Ошибки по полям из `422 MANDATE_INVALID`.
 *
 * Код без перевода не показывается: сырой `TARGET_TOO_LOW` под полем — утечка
 * внутренностей, поэтому есть общий запасной текст.
 */
function fieldErrors(error: unknown, t: (key: string) => string): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const result: Record<string, string> = {};
  for (const item of error.fieldErrors) {
    const key = `negotiation.mandate.error.${item.code}`;
    const translated = t(key);
    result[item.field] = translated === key ? t('negotiation.mandate.error.UNKNOWN') : translated;
  }
  return result;
}

/**
 * N-02 «Карточка переговоров» (FE-173, FE-174, FE-460, FE-461; MVP_PRD §21.5,
 * §22.3, §22.7; решение заказчика от 12.09.2026).
 *
 * Экран — это шаг 5 воронки: в шапке трекер семи шагов и рельс Karl, как на
 * четвёрке. Содержимое идёт **сверху вниз в порядке событий**:
 *
 * 1. **опросник** — пока автомобиль можно выбрать (`next_actor = check_done`):
 *    условия, максимальная цена рядом с открытыми пунктами проверки, свои
 *    вопросы, подтверждение. Уходит одним запросом вместе с выбором (§21),
 *    и после него опросник исчезает;
 * 2. **лента торга** — что Karl написал, что ответил автохаус (по пунктам, с
 *    цитатами), итог «было → стало» и решение: согласиться, ответить новым
 *    максимумом или остановить. Переписки целиком здесь нет — копии писем в
 *    почте; на экране только разобранное Karl'ом;
 * 3. **ответы автохауса на вопросы проверки** и как он отвечал.
 *
 * Экран собирает и ничего не решает: доступность действий приходит с сервера
 * (`available_actions`), чей ход — тоже (`next_actor`). Необратимые действия —
 * остановка, согласие, отказ — в два шага и без оптимистичного UI (CLAUDE.md
 * §8): каждое уходит письмом автохаусу.
 *
 * Редизайн 2026-10 (DESIGN_SYSTEM §5.1, §7): **красная кнопка на экране одна** —
 * главное необратимое действие состояния (согласие с предложением, отправка
 * в опроснике, переход к контактам после согласия). «Остановить торг» —
 * деструктивная, бордовым контуром; остальное — вторичные. Вводного абзаца у
 * экрана нет — под заголовком строка автохауса и чей ход, — поэтому нет и
 * `KarlIntro`: реплики Karl живут в самой ленте торга.
 */
export function NegotiationPage() {
  const { t } = useTranslation();
  const { negotiationId = '' } = useParams();
  const queryClient = useQueryClient();

  const query = useNegotiation(negotiationId || null);
  const metrics = useNegotiationMetrics(negotiationId || null);
  const patch = usePatchNegotiation(negotiationId);
  const messageAction = useMessageAction(negotiationId);
  const createMandate = useCreateMandate(negotiationId);
  const config = useConfig();
  const [stopping, setStopping] = useState(false);
  const [countering, setCountering] = useState(false);

  // Идентификаторы заказа и автомобиля известны только после загрузки
  // карточки, а хуки условными быть не могут: запросы включаются флагами.
  const detail = query.data;
  const orderId = detail?.search_session_id ?? null;
  const vehicleId = detail?.vehicle.vehicle_id ?? null;
  const canChoose =
    detail !== undefined &&
    detail.status === 'active' &&
    detail.next_actor === 'check_done' &&
    orderId !== null;
  const sessionQuery = useSearchSession(orderId);
  const conclusion = useConclusion(orderId);
  const acknowledge = useAcknowledgeDiscrepancy(orderId ?? '');
  const previewQuery = useNegotiationPreview(canChoose || countering ? vehicleId : null);
  const selectionQuery = useSelection(canChoose ? orderId : null);
  const selectVehicle = useSelectVehicle(orderId ?? '');

  // Шаг 5 в трекере: страница не рисует навигацию, а сообщает оболочке стадию
  // заказа (CLAUDE.md §8).
  //
  // Рельс Karl несёт диалог **заказа** (FE-581), а в адресе переписки
  // (`/app/negotiations/:id`) заказа нет — его номер берётся из самой
  // переписки. Пока карточка грузится, рельс уже стоит, а диалог в нём —
  // скелетоном: иначе колонка появлялась бы рывком и сдвигала экран. Рельса нет,
  // когда заказа нет: переписка пережила удаление заказа (MVP_TECH_RFC §23.10)
  // или не нашлась. Диалога, который рельс мог бы нести, тогда не существует —
  // это экран вне заказа, как список переписок (DESIGN_SYSTEM §4.3).
  //
  // Заказ уходит оболочке и для трекера: пройденные шаги ведут на экраны
  // заказа, а шаг 5 — на эту же переписку.
  useAppShell({
    stage: sessionQuery.data?.session.stage ?? null,
    paid: sessionQuery.data?.session.paid ?? false,
    karl:
      query.isPending || orderId !== null
        ? { extra: <KarlDialogue searchId={orderId ?? ''} /> }
        : null,
    searchId: orderId,
    negotiationId: orderId === null ? null : negotiationId,
  });

  if (query.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="50%" />
        <Skeleton height="16rem" />
      </div>
    );
  }

  if (query.isError || detail === undefined) {
    const missing = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div className={styles.page}>
        <StateBlock
          tone={missing ? 'neutral' : 'error'}
          title={missing ? t('negotiation.detail.missingTitle') : t('negotiation.list.errorTitle')}
          description={
            missing ? t('negotiation.detail.missingText') : t('negotiation.list.errorText')
          }
          action={
            <ButtonLink to={ROUTES.negotiations} variant="secondary">
              {t('negotiation.detail.backToList')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  const mandate = currentMandate(detail);
  const offer = decidableOffer(detail);
  const findings = checkFindings(detail);
  const can = (action: string) => detail.available_actions.includes(action as never);
  // Блок решения держится на **любом** из двух действий (BE-590): сервер
  // скрывает «Согласиться» там, где согласие ответило бы `409`, но отказаться
  // от предложения пользователь может и тогда.
  const canAccept = can('accept_offer');
  const decisionPending = offer !== null && (canAccept || can('decline_offer'));
  // Заключение по сделке принадлежит заказу, а не переписке: переписка
  // переживает удаление заказа (§23.10), и тогда сверять уже нечего.
  const finalCheck =
    conclusion.data?.items.find((item) => item.vehicle_id === detail.vehicle.vehicle_id)
      ?.final_check ?? null;
  const pendingMessage = detail.messages.find(
    (message) => message.awaiting_approval || message.cancellable,
  );
  const showAwaitingPanel =
    detail.status === 'awaiting_user' && detail.awaiting_actions.length > 0 && !decisionPending;
  const successFee = config.data?.success_fee;
  const fee: FeeTerms | null =
    successFee !== undefined && successFee.enabled
      ? { rate_bps: successFee.rate_bps, flat_cents: successFee.flat_cents }
      : null;
  const preview = previewQuery.data ?? null;
  const selection = selectionQuery.data ?? null;

  const refreshAfterMandate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.negotiation(negotiationId) });
    if (orderId !== null) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(orderId) });
    }
  };

  return (
    <div className={styles.page}>
      <Link
        to={orderId === null ? ROUTES.negotiations : ROUTES.searchTop4(orderId)}
        className={styles.back}
      >
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {orderId === null ? t('negotiation.detail.backToList') : t('negotiation.detail.backToTop4')}
      </Link>

      <header className={styles.header}>
        <p className={styles.badges}>
          <NegotiationStatusBadge status={detail.status} />
          <Badge tone="neutral">{t(phaseKey(detail.phase))}</Badge>
        </p>
        <h1 className={styles.title}>
          {detail.vehicle.make} {detail.vehicle.model}
          {detail.vehicle.variant === null ? null : ` · ${detail.vehicle.variant}`}
        </h1>
        <p className={styles.subtitle}>
          {detail.vehicle.seller_name ?? t('negotiation.list.unknownSeller')}
          {detail.vehicle.seller_city === null ? null : ` · ${detail.vehicle.seller_city}`}
        </p>
        <p className={styles.actor}>{t(nextActorKey(detail.next_actor))}</p>

        {/* Ход проверки: что уже есть по вопросам (§36.6). Числа с сервера;
            пределов писем и вопросов на экране нет (12.09.2026). */}
        {detail.check_progress === null ? null : (
          <p className={styles.progress} role="status">
            {t(
              detail.check_progress.complete
                ? 'negotiation.check.progressDone'
                : detail.check_progress.awaiting_reply
                  ? 'negotiation.check.progressWaiting'
                  : 'negotiation.check.progressAsking',
              {
                answered: detail.check_progress.answered,
                refused: detail.check_progress.refused,
                open: detail.check_progress.unanswered,
              },
            )}
          </p>
        )}

        {/* Причина ожидания — «требует внимания»; причина закрытия — факт, а
            не тревога: нейтральная плашка. Иконка и текст — всегда (§2.4). */}
        {detail.awaiting_reason === null || decisionPending ? null : (
          <p className={styles.reason} role="status">
            <WarningCircleIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
            <span>{t(awaitingReasonKey(detail.awaiting_reason))}</span>
          </p>
        )}
        {detail.close_reason === null ? null : (
          <p className={styles.closed} role="status">
            <InfoIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
            <span>{t(closeReasonKey(detail.close_reason))}</span>
          </p>
        )}
      </header>

      {/* Индикация NEGOTIATION_DRY_RUN и выключенной автономности (FE-180, §25):
          «переговоры идут» и «письма не отправляются» перепутать нельзя. */}
      {config.data?.negotiations?.dry_run === true ? (
        <p className={styles.reason} role="status">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          <span>{t('negotiation.detail.dryRun')}</span>
        </p>
      ) : null}
      {config.data?.negotiations?.autonomy_enabled === false ? (
        <p className={styles.reason} role="status">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          <span>{t('negotiation.detail.autonomyOff')}</span>
        </p>
      ) : null}

      {/* --- Опросник перед торгом (FE-460): выбор автомобиля и первый мандат --- */}
      {canChoose ? (
        previewQuery.isPending || selectionQuery.isPending ? (
          <Skeleton height="14rem" radius="var(--radius-card)" />
        ) : preview === null || selection === null ? (
          <StateBlock
            tone="error"
            title={t('negotiation.setup.loadErrorTitle')}
            description={t('negotiation.setup.loadErrorText')}
          />
        ) : (
          <BargainingSetup
            mode="select"
            preview={preview}
            // `preview.can_start` здесь не гейт: предпросмотр отвечает на
            // вопрос «можно ли завести **новую** переписку», а она уже заведена
            // — проверкой по всем четырём. Его отказы `NEGOTIATION_ALREADY_EXISTS`
            // и `DEALER_THREAD_LIMIT_REACHED` на этом экране истинны всегда и
            // по устройству воронки, а не по беде. Предпосылки выбора автомобиля
            // проверяет `POST /select-vehicle` (§34.4), и его отказ виден ниже.
            currency={detail.listing_price.currency}
            findings={findings}
            checkProgress={detail.check_progress}
            maxQuestions={selection.max_custom_questions}
            busy={selectVehicle.isPending}
            fieldErrors={fieldErrors(selectVehicle.error, t)}
            fee={fee}
            onSubmit={(input) => {
              selectVehicle.mutate(
                {
                  vehicleId: detail.vehicle.vehicle_id,
                  mandate: input,
                  feeConsentHash: selection.fee_consent.text_hash,
                },
                { onSuccess: refreshAfterMandate },
              );
            }}
          />
        )
      ) : null}
      {canChoose && selectVehicle.error !== null && actionError(selectVehicle.error, t) !== null ? (
        <p className={styles.error} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          <span>{actionError(selectVehicle.error, t)}</span>
        </p>
      ) : null}

      {/* --- Лента торга (FE-461) --------------------------------------------- */}
      {mandate === null ? null : (
        <BargainingFeed
          detail={detail}
          mandate={mandate}
          findings={findings}
          offer={offer}
          fee={fee}
          decisionPending={decisionPending}
          decision={
            offer === null ? null : (
              <>
                {/* Финальная сверка **над кнопками решения** (FE-250, MVP_PRD
                    §38.3): расхождение нельзя пропустить, прокрутив страницу.
                    Существенное расхождение приостанавливает согласие до
                    подтверждения — правило живёт в сервисном слое. */}
                {finalCheck === null ? null : (
                  <FinalCheckBlock
                    check={finalCheck}
                    pending={acknowledge.isPending}
                    onAcknowledge={
                      orderId === null
                        ? undefined
                        : () => {
                            acknowledge.mutate(detail.vehicle.vehicle_id);
                          }
                    }
                  />
                )}
                {countering ? null : (
                  <OfferDecision
                    detail={detail}
                    offer={offer}
                    mandate={mandate}
                    acceptBlocked={acceptanceBlocked(finalCheck)}
                    acceptAvailable={canAccept}
                    acceptanceRefusal={detail.acceptance_refusal}
                  />
                )}
                {/* Встречное предложение: новый максимум — новая версия
                    мандата, и Karl пишет автохаусу снова (§22.7). */}
                {countering ? (
                  preview === null ? (
                    <Skeleton height="10rem" radius="var(--radius-card)" />
                  ) : (
                    <BargainingSetup
                      mode="counter"
                      preview={preview}
                      currency={detail.listing_price.currency}
                      findings={findings}
                      checkProgress={detail.check_progress}
                      maxQuestions={mandate.custom_questions.length}
                      initial={mandate}
                      busy={createMandate.isPending}
                      fieldErrors={fieldErrors(createMandate.error, t)}
                      fee={null}
                      onSubmit={(input) => {
                        createMandate.mutate(input, {
                          onSuccess: () => {
                            setCountering(false);
                            refreshAfterMandate();
                          },
                        });
                      }}
                      onCancel={() => {
                        setCountering(false);
                      }}
                    />
                  )
                ) : (
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      disabled={patch.isPending}
                      onClick={() => {
                        setCountering(true);
                      }}
                    >
                      {t('negotiation.feed.counter.open')}
                    </Button>
                  </div>
                )}
              </>
            )
          }
        />
      )}

      {/* Экран ожидания решения для причин не про цену (FE-180, §22.5). */}
      {showAwaitingPanel ? (
        <AwaitingUserPanel
          detail={detail}
          busy={patch.isPending}
          onStop={() => {
            setStopping(true);
          }}
          onResume={() => {
            patch.mutate('resume');
          }}
          onUpdateMandate={
            mandate === null
              ? null
              : () => {
                  setCountering(true);
                }
          }
        />
      ) : null}

      {/* Управление письмом, которое ждёт подтверждения или стоит в окне
          отмены (§23.7, §22.4): только в режиме подтверждения — письма торга
          уходят сразу. */}
      {pendingMessage === undefined ? null : (
        <section className={styles.pending}>
          <p className={styles.pendingText} role="timer">
            {pendingMessage.awaiting_approval ? (
              t('negotiation.detail.awaitingApproval')
            ) : (
              <ScheduledCountdown scheduledAt={pendingMessage.scheduled_send_at} />
            )}
          </p>
          <div className={styles.actions}>
            {pendingMessage.awaiting_approval && can('approve_message') ? (
              <Button
                disabled={messageAction.isPending}
                onClick={() => {
                  messageAction.mutate({
                    messageId: pendingMessage.message_id,
                    action: 'approve',
                  });
                }}
              >
                {t('negotiation.detail.approve')}
              </Button>
            ) : null}
            {pendingMessage.cancellable && can('send_now') ? (
              <Button
                variant="secondary"
                disabled={messageAction.isPending}
                onClick={() => {
                  messageAction.mutate({
                    messageId: pendingMessage.message_id,
                    action: 'send-now',
                  });
                }}
              >
                {t('negotiation.detail.sendNow')}
              </Button>
            ) : null}
            {pendingMessage.cancellable && can('cancel_message') ? (
              <Button
                variant="secondary"
                disabled={messageAction.isPending}
                onClick={() => {
                  messageAction.mutate({
                    messageId: pendingMessage.message_id,
                    action: 'cancel',
                  });
                }}
              >
                {t('negotiation.detail.cancelMessage')}
              </Button>
            ) : null}
          </div>
        </section>
      )}

      {/* В `awaiting_user` действия задаёт панель решения (≤ 3 вариантов,
          §22.5); общий ряд кнопок рядом с ней дублировал бы те же действия. */}
      {showAwaitingPanel ? null : (
        <div className={styles.actions}>
          {(['pause', 'resume'] as NegotiationPatchAction[]).map((action) =>
            can(action) ? (
              <Button
                key={action}
                variant="secondary"
                disabled={patch.isPending}
                onClick={() => {
                  patch.mutate(action);
                }}
              >
                {t(`negotiation.detail.${action}`)}
              </Button>
            ) : null,
          )}
          {can('stop') && !stopping ? (
            <Button
              variant="danger"
              disabled={patch.isPending}
              onClick={() => {
                setStopping(true);
              }}
            >
              {t('negotiation.detail.stop')}
            </Button>
          ) : null}
        </div>
      )}

      {/* Остановка терминальна: вернуться к автомобилю можно только новой
          перепиской и только при свободном слоте (§22.2). Поэтому второй шаг. */}
      {stopping ? (
        <section className={styles.confirm} role="group">
          <p className={styles.pendingText}>{t('negotiation.detail.stopConfirm')}</p>
          <div className={styles.actions}>
            <Button
              variant="danger"
              disabled={patch.isPending}
              onClick={() => {
                patch.mutate('stop', {
                  onSuccess: () => {
                    setStopping(false);
                  },
                });
              }}
            >
              {patch.isPending ? t('negotiation.detail.pending') : t('negotiation.detail.stop')}
            </Button>
            <Button
              variant="ghost"
              disabled={patch.isPending}
              onClick={() => {
                setStopping(false);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      ) : null}

      {/* Ответы автохауса на вопросы проверки — разобранные Karl'ом, с
          цитатами (FE-230, §36.4). Переписки целиком на экране нет. */}
      <CheckAnswers detail={detail} />
      {/* Профиль коммуникации — отдельным блоком (FE-240, §37.3): «что сказал
          автохаус» и «как он отвечал» — разные вопросы. */}
      <SellerMetrics metrics={metrics.data ?? null} />
    </div>
  );
}
